/* eslint-disable react-hooks/exhaustive-deps */
import { FC, PropsWithChildren, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSetIsLoggedIn, useSetUser } from '@zoopla/user';
import { InputField } from '@zoopla-eng/zds-components/input-field';
import { Link } from '@zoopla-eng/zds-components/link';
import { Checkbox } from '@zoopla-eng/zds-components/checkbox';
import { Box } from '@zoopla-eng/zds-components/box';
import { Button } from '@zoopla-eng/zds-components/button';

import SignInFormData from './../../interfaces/SignInFormData';
import { EMAIL_REGEX, PASSWORD_MIN_LENGTH } from '../../validation/rules';
import { useSignIn } from './hooks/useSignIn';
import { StyledGeneralError } from '../../AuthModal.styles';
import { AuthModalStepKeys, SocialAuthProps } from '../../AuthModal.types';
import triggerTracking from '@blueprint/tracking';
import SocialAuth from './SocialAuth';
import { User } from '../../../../graphql.generated';
import { trackAuthSegmentEvent } from '../../utils/segmentTrack';
import { liverampAts } from '../../utils/liverampAts';
import {
    trackFailureEmail,
    trackKeepMeSignedIn,
    trackSuccessSignIn
} from '../../AuthModal.tracking';
import { InputPassword } from '../../InputPassword';
import {
    shouldShowYourSituation,
    updateYourSituation,
    UpdateYourSituationType
} from '../../utils';
import { useSaveGuestSituationData } from '../../hooks/useSaveGuestSituationData';

const detailsEmailFieldLabelTn = 'Email address';
const detailsEmailFieldValidationTn = 'Please enter a valid email address';
const detailsPasswordFieldLabelTn = 'Password';
const forgotPasswordTn = 'Forgot password';
const detailsPasswordFieldGuidanceTn = 'Minimum 8 characters';
const signinButtonTn = 'Sign in';
const socialSignInTextTn = 'or sign in with your email';
const googleSignInTextTn = 'Google';
const facebookSignInTextTn = 'Facebook';

const socialCopy = {
    cta: socialSignInTextTn,
    facebook: facebookSignInTextTn,
    google: googleSignInTextTn
};

const SignInForm: FC<
    PropsWithChildren<
        Pick<
            SocialAuthProps,
            | 'close'
            | 'actionAfterAuth'
            | 'resetActionAfterAuth'
            | 'onSocialAuth'
            | 'shouldFetchUser'
            | 'setCurrentStep'
        >
    >
> = ({
    close,
    actionAfterAuth,
    resetActionAfterAuth = () => null,
    onSocialAuth,
    setCurrentStep
}) => {
    const useLiveRampAts = true;

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        reset,
        getValues,
        control
    } = useForm<SignInFormData>({
        mode: 'onBlur',
        reValidateMode: 'onBlur'
    });

    const setUser = useSetUser();
    const setLoggedIn = useSetIsLoggedIn();
    const { signInUser, generalError, resetGeneralError, getUserData } =
        useSignIn({
            onSuccess: () => null
        });
    const { saveSituationData, hasSavedAnswers, removeSavedAnswers } =
        useSaveGuestSituationData();

    const onSignInSuccess = async (user?: User) => {
        setLoggedIn(true);

        try {
            const userProfileStorage =
                sessionStorage.getItem('auth-user-profile');
            const userProfileData = JSON.parse(userProfileStorage || '{}');
            const situationStatus = updateYourSituation(userProfileData);

            if (situationStatus === UpdateYourSituationType.SELLER_FOLLOWUP) {
                setCurrentStep?.(
                    AuthModalStepKeys.UPDATE_YOUR_SITUATION_SELLER
                );
                return;
            }

            if (situationStatus === UpdateYourSituationType.NEEDS_UPDATE) {
                setCurrentStep?.(AuthModalStepKeys.UPDATE_YOUR_SITUATION);
                return;
            }

            if (
                situationStatus === UpdateYourSituationType.NO_SITUATION_ANSWER
            ) {
                setCurrentStep?.(AuthModalStepKeys.YOUR_SITUATION);
                return;
            }

            if (shouldShowYourSituation(userProfileData)) {
                setCurrentStep?.(AuthModalStepKeys.YOUR_SITUATION);
                return;
            }

            if (hasSavedAnswers()) {
                await saveSituationData();
                removeSavedAnswers();
            }
        } catch (e) {
            console.error(e);
        }

        await actionAfterAuth?.action();
        resetActionAfterAuth();

        close(user);
    };

    const getIsRememberMeChecked = () => getValues('rememberMe');

    const onSocialAuthSuccess = async (user?: User) => {
        const userData = {
            traits: user?.traits,
            profile: user?.profile
        };
        sessionStorage?.setItem('auth-user-profile', JSON.stringify(userData));

        if (user) {
            if (useLiveRampAts && user.email) {
                liverampAts(user.email);
            }
            setUser(user);
        }

        if (typeof onSocialAuth === 'function') {
            if (getIsRememberMeChecked()) {
                trackKeepMeSignedIn(actionAfterAuth?.eventLabel);
            }
            onSocialAuth(user);
        } else {
            close();
        }
    };

    const onSubmit = handleSubmit(async (formData) => {
        const payload = await signInUser({
            ...formData,
            rememberMe: !!formData.rememberMe
        });

        if (!payload?.data?.signinUser) {
            return;
        }

        if (payload.data.signinUser.errorCode === 'PASSWORD') {
            trackFailureEmail(actionAfterAuth?.eventLabel);
        }

        if (payload.data.signinUser.success) {
            if (useLiveRampAts) {
                liverampAts(formData.email);
            }

            const user = (await getUserData())?.data?.user;

            const userData = {
                traits: user?.traits,
                profile: user?.profile
            };
            sessionStorage?.setItem(
                'auth-user-profile',
                JSON.stringify(userData)
            );

            trackSuccessSignIn(actionAfterAuth?.eventLabel);
            if (user) {
                trackAuthSegmentEvent('signin', actionAfterAuth?.segment, user);
            }

            if (formData.rememberMe) {
                trackKeepMeSignedIn(actionAfterAuth?.eventLabel);
            }

            onSignInSuccess();
        }
    });

    const passedActionAfterAuth = {
        action: onSocialAuthSuccess,
        eventLabel: actionAfterAuth?.eventLabel || 'register_modal',
        segment: actionAfterAuth?.segment
    };

    const resetFormErrors = () => {
        // reset signin form general and field-specific errors
        resetGeneralError();
        const currentValues = getValues();

        reset({
            ...currentValues
        });
    };

    useEffect(() => {
        reset();
    }, []);

    return (
        <>
            <SocialAuth
                copy={socialCopy}
                type="signin"
                shouldReset={isSubmitting}
                resetErrors={resetFormErrors}
                actionAfterAuth={passedActionAfterAuth}
                isRememberMeChecked={getIsRememberMeChecked()}
            />
            <form
                onSubmit={onSubmit}
                noValidate
                data-testid="auth-modal-signin-form"
                method="post"
            >
                <div role="alert">
                    {generalError && (
                        <Box pb="$S500">
                            <StyledGeneralError data-testid="general-error">
                                {generalError}
                            </StyledGeneralError>
                        </Box>
                    )}
                </div>
                <Box pb="$S500">
                    <InputField
                        label={detailsEmailFieldLabelTn}
                        aria-required
                        type="email"
                        errorText={
                            errors.email &&
                            (errors.email.message ||
                                detailsEmailFieldValidationTn)
                        }
                        {...register('email', {
                            required: true,
                            pattern: EMAIL_REGEX
                        })}
                        data-testid="email-field-input"
                    />
                </Box>
                <Box pb="$S500">
                    <InputPassword
                        label={detailsPasswordFieldLabelTn}
                        aria-required
                        errorText={
                            errors.password &&
                            (errors.password.message ||
                                detailsPasswordFieldGuidanceTn)
                        }
                        {...register('password', {
                            required: true,
                            minLength: PASSWORD_MIN_LENGTH
                        })}
                        data-testid="password-field"
                    />
                </Box>
                <Box pb="$M100" bpL100={{ pb: '$M300' }}>
                    <Link
                        data-testid="forgot-password-link"
                        href="/signin/forgot/"
                        onClick={() =>
                            triggerTracking({
                                event: 'GAEvent',
                                eventCategory: 'signin',
                                eventAction: 'click_forgotten_password',
                                eventLabel:
                                    actionAfterAuth?.eventLabel ||
                                    'signin_modal'
                            })
                        }
                    >
                        {forgotPasswordTn}
                    </Link>
                </Box>
                <Box pb="$M100">
                    <Controller
                        control={control}
                        name="rememberMe"
                        render={({ field: { onChange, onBlur, name } }) => (
                            <Checkbox
                                label="Keep me signed in for 7 days"
                                data-testid="keep-me-signed-in-field"
                                aria-describedby="keep-me-sign-in"
                                checked={getValues('rememberMe')}
                                onCheckedChange={onChange}
                                onBlur={onBlur}
                                name={name}
                                helpText="If you're using a public device, please skip."
                            />
                        )}
                    />
                </Box>
                <Box display="flex" justifyContent="center">
                    <Box width="100%" bpM100={{ width: '50%' }}>
                        <Button
                            category="primary"
                            loading={isSubmitting}
                            loadingText="Signing in"
                            data-testid="signin-button"
                            fw
                        >
                            {signinButtonTn}
                        </Button>
                    </Box>
                </Box>
            </form>
        </>
    );
};

export default SignInForm;
